import type {
  FsyAnalysisSection,
  FsyAnalysisTalk,
  FsyContentBlock,
  FsyItem,
  FsyMonth,
} from '../../data/schema';
import {
  formatFsySunday,
  getUpcomingFsySunday,
  type ScheduledFsySunday,
} from './sundaySchedule';

function routerLink(
  href: string,
  label: string,
  className?: string,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.dataset.router = '';
  link.textContent = label;
  if (className) link.className = className;
  return link;
}

function externalAttribution(
  sourceUrl: string,
  linkText: string,
): HTMLElement {
  const attribution = document.createElement('p');
  attribution.className = 'fsy-attribution';
  const label = document.createElement('span');
  label.textContent = 'Source: ';
  const link = document.createElement('a');
  link.href = sourceUrl;
  link.target = '_blank';
  link.rel = 'noreferrer';
  link.textContent = linkText;
  attribution.append(label, link);
  return attribution;
}

function createMonthAttribution(month: FsyMonth): HTMLElement {
  return externalAttribution(month.attribution.sourceUrl, month.attribution.text);
}

function createItemAttribution(item: FsyItem): HTMLElement {
  return externalAttribution(
    item.sourceUrl,
    `${item.title}, For the Strength of Youth`,
  );
}

function createUpcomingBadge(
  schedule: ScheduledFsySunday,
  compact = false,
): HTMLElement {
  const badge = document.createElement('p');
  badge.className = compact
    ? 'fsy-upcoming fsy-upcoming--compact'
    : 'fsy-upcoming';
  badge.dataset.upcomingLesson = '';
  const prefix = schedule.repeatsGuideStudy
    ? 'Upcoming lesson, FSY guide study repeats'
    : 'Upcoming lesson';
  badge.textContent = `${prefix}: ${formatFsySunday(schedule.date)}`;
  return badge;
}

function createAnalysis(talks: FsyAnalysisTalk[]): HTMLDetailsElement {
  const details = document.createElement('details');
  details.className = 'fsy-analysis';
  const summary = document.createElement('summary');
  summary.textContent = 'Analysis & Discovery';
  const list = document.createElement('div');
  list.className = 'fsy-analysis__list';

  for (const talk of talks) {
    const link = routerLink(talk.href, '');
    link.className = 'fsy-analysis-talk';
    link.dataset.talkId = talk.talkId;
    link.dataset.paragraphId = talk.paragraphId;

    const heading = document.createElement('h4');
    heading.textContent = talk.title;
    const speaker = document.createElement('p');
    speaker.className = 'fsy-analysis-talk__speaker';
    speaker.textContent = talk.speaker;
    const relevance = document.createElement('p');
    relevance.className = 'fsy-analysis-talk__relevance';
    relevance.textContent = talk.relevance;
    link.append(heading, speaker, relevance);
    list.append(link);
  }

  details.append(summary, list);
  return details;
}

function createScriptureTable(
  block: Extract<FsyContentBlock, { type: 'scripture-table' }>,
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'fsy-scripture-table-wrap';
  const table = document.createElement('table');
  table.className = 'fsy-scripture-table';
  const head = document.createElement('thead');
  const headingRow = document.createElement('tr');
  for (const column of block.columns) {
    const cell = document.createElement('th');
    cell.scope = 'col';
    cell.textContent = column;
    headingRow.append(cell);
  }
  head.append(headingRow);

  const body = document.createElement('tbody');
  for (const scripture of block.scriptures) {
    const row = document.createElement('tr');
    const reference = document.createElement('th');
    reference.scope = 'row';
    reference.textContent = scripture;
    const messenger = document.createElement('td');
    const keys = document.createElement('td');
    messenger.className = 'fsy-scripture-table__blank';
    keys.className = 'fsy-scripture-table__blank';
    row.append(reference, messenger, keys);
    body.append(row);
  }
  table.append(head, body);
  wrapper.append(table);
  return wrapper;
}

function sectionFor(
  sections: FsyAnalysisSection[],
  blockIndex: number,
  itemIndex?: number,
): FsyAnalysisSection | undefined {
  return sections.find(
    (section) =>
      section.blockIndex === blockIndex && section.itemIndex === itemIndex,
  );
}

function createContentBlocks(
  blocks: FsyContentBlock[],
  analysisSections: FsyAnalysisSection[],
): DocumentFragment {
  const fragment = document.createDocumentFragment();
  for (const [blockIndex, block] of blocks.entries()) {
    let rendered: HTMLElement;
    if (block.type === 'paragraph') {
      const paragraph = document.createElement('p');
      paragraph.textContent = block.text;
      rendered = paragraph;
    } else if (block.type === 'heading') {
      const heading = document.createElement('h3');
      heading.textContent = block.text;
      rendered = heading;
    } else if (block.type === 'quote') {
      const quote = document.createElement('blockquote');
      quote.className = 'fsy-source-quote';
      quote.textContent = block.text;
      rendered = quote;
    } else if (block.type === 'scripture-table') {
      rendered = createScriptureTable(block);
    } else {
      const section = document.createElement('section');
      if (block.type === 'resources') {
        const heading = document.createElement('h3');
        heading.textContent = block.title;
        section.append(heading);
      }
      const list = document.createElement('ul');
      list.className =
        block.type === 'resources' ? 'fsy-resource-list' : 'fsy-source-list';
      for (const [itemIndex, itemText] of block.items.entries()) {
        const item = document.createElement('li');
        const text = document.createElement('span');
        text.textContent = itemText;
        item.append(text);
        const itemAnalysis = sectionFor(
          analysisSections,
          blockIndex,
          itemIndex,
        );
        if (itemAnalysis && itemAnalysis.talks.length > 0) {
          item.append(createAnalysis(itemAnalysis.talks));
        }
        list.append(item);
      }
      section.append(list);
      rendered = section;
    }
    fragment.append(rendered);
    const blockAnalysis = sectionFor(analysisSections, blockIndex);
    if (blockAnalysis && blockAnalysis.talks.length > 0) {
      fragment.append(createAnalysis(blockAnalysis.talks));
    }
  }
  return fragment;
}

const COMPLETION_KEY_PREFIX = 'fsy-completed:v1';

export function fsyCompletionKey(monthId: string, itemId: string): string {
  return `${COMPLETION_KEY_PREFIX}:${monthId}:${itemId}`;
}

function defaultStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isCompleted(
  monthId: string,
  itemId: string,
  storage: Storage | null,
): boolean {
  return storage?.getItem(fsyCompletionKey(monthId, itemId)) === 'true';
}

function itemLabel(item: FsyItem): string {
  if (item.type === 'intro') return 'Monthly topic';
  if (item.type === 'activity-idea') {
    return item.seriesLabel ?? 'Youth activity idea';
  }
  if (item.seriesLabel) {
    return `Week ${String(item.week)} · ${item.seriesLabel}`;
  }
  const audience = item.audienceLabel ? ` · ${item.audienceLabel}` : '';
  return `Week ${String(item.week)} · ${item.scheduleLabel ?? ''}${audience}`;
}

function createItem(
  monthId: string,
  item: FsyItem,
  upcoming: ScheduledFsySunday | null,
  storage: Storage | null,
): HTMLElement {
  const article = document.createElement('details');
  const categoryClass =
    item.type === 'intro'
      ? 'fsy-month-intro'
      : item.type === 'activity-idea'
        ? 'fsy-activity'
        : 'fsy-lesson';
  article.className = `${categoryClass} card`;
  article.id = item.id;
  article.dataset.itemType = item.type;
  article.dataset.slug = item.slug;
  if (item.week) article.dataset.week = String(item.week);
  const completed = isCompleted(monthId, item.id, storage);
  article.open = !completed;
  if (completed) article.dataset.completed = '';

  const isUpcoming = Boolean(item.week && upcoming?.week === item.week);
  if (isUpcoming) {
    article.classList.add('fsy-lesson--upcoming');
    article.dataset.upcoming = '';
  }

  const header = document.createElement('summary');
  header.className = 'fsy-lesson__header';
  const headingCopy = document.createElement('div');
  headingCopy.className = 'fsy-lesson__heading-copy';
  const label = document.createElement('p');
  label.className = 'eyebrow';
  label.textContent = itemLabel(item);
  const title = document.createElement('h2');
  title.textContent = item.title;
  headingCopy.append(label, title);
  header.append(headingCopy);
  if (isUpcoming && upcoming) {
    header.append(createUpcomingBadge(upcoming, true));
  }

  const completionLabel = document.createElement('label');
  completionLabel.className = 'fsy-completion';
  completionLabel.addEventListener('click', (event) => event.stopPropagation());
  const completion = document.createElement('input');
  completion.type = 'checkbox';
  completion.checked = completed;
  completion.setAttribute('aria-label', `Mark ${item.title} completed`);
  const completionText = document.createElement('span');
  completionText.textContent = 'Completed';
  completion.addEventListener('change', () => {
    if (completion.checked) {
      storage?.setItem(fsyCompletionKey(monthId, item.id), 'true');
      article.dataset.completed = '';
      article.open = false;
    } else {
      storage?.removeItem(fsyCompletionKey(monthId, item.id));
      delete article.dataset.completed;
      article.open = true;
    }
  });
  completionLabel.append(completion, completionText);
  header.append(completionLabel);

  const body = document.createElement('div');
  body.className = 'fsy-item__body';
  const content = document.createElement('div');
  content.className = 'fsy-lesson__content';
  content.append(createContentBlocks(item.blocks, item.analysisSections));
  body.append(content, createItemAttribution(item));
  article.append(header, body);
  return article;
}

function createMonthNavigation(
  months: FsyMonth[],
  selectedMonthId?: string,
): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = 'fsy-month-nav';
  nav.setAttribute('aria-label', 'FSY months');
  const list = document.createElement('div');
  list.className = 'fsy-month-nav__tabs';
  for (const month of months) {
    const link = routerLink(`/fsy/${month.id}`, month.monthLabel, 'fsy-month-tab');
    link.dataset.monthId = month.id;
    if (month.id === selectedMonthId) {
      link.classList.add('fsy-month-tab--active');
      link.setAttribute('aria-current', 'page');
    }
    list.append(link);
  }
  nav.append(list);
  return nav;
}

export function createFsyIndexPage(
  months: FsyMonth[],
  today = new Date(),
): HTMLElement {
  const page = document.createElement('div');
  page.className = 'fsy-index';
  page.dataset.route = 'fsy-index';

  const hero = document.createElement('header');
  hero.className = 'fsy-hero page-width';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow';
  eyebrow.textContent = 'Youth Sunday lessons';
  const title = document.createElement('h1');
  title.textContent = 'For the Strength of Youth';
  const intro = document.createElement('p');
  intro.textContent =
    'Monthly lesson plans connected to April 2026 general conference.';
  hero.append(eyebrow, title, intro);
  hero.append(createMonthNavigation(months));

  const grid = document.createElement('section');
  grid.className = 'fsy-month-grid page-width';
  grid.setAttribute('aria-label', 'Monthly lesson plans');
  for (const month of months) {
    const upcoming = getUpcomingFsySunday(today, month.year, month.month);
    const upcomingItems = upcoming
      ? month.items.filter((item) => item.week === upcoming.week)
      : [];
    const card = document.createElement('article');
    card.className = 'fsy-month-card card';
    card.dataset.monthId = month.id;
    if (upcoming) card.dataset.upcoming = '';

    const label = document.createElement('p');
    label.className = 'eyebrow';
    label.textContent = month.monthLabel;
    const heading = document.createElement('h2');
    heading.append(
      routerLink(`/fsy/${month.id}`, month.topic, 'fsy-month-card__link'),
    );
    const reference = document.createElement('p');
    reference.className = 'fsy-month-card__reference';
    reference.textContent = month.guideReference;
    card.append(label, heading, reference);
    if (upcoming && upcomingItems.length > 0) {
      const upcomingBlock = document.createElement('div');
      upcomingBlock.className = 'fsy-month-card__upcoming';
      upcomingBlock.append(createUpcomingBadge(upcoming, true));
      for (const item of upcomingItems) {
        const itemTitle = document.createElement('p');
        itemTitle.textContent = item.audienceLabel
          ? `${item.audienceLabel}: ${item.title}`
          : item.title;
        upcomingBlock.append(itemTitle);
      }
      card.append(upcomingBlock);
    }
    card.append(createMonthAttribution(month));
    grid.append(card);
  }

  page.append(hero, grid);
  return page;
}

export function createFsyMonthPage(
  month: FsyMonth,
  today = new Date(),
  months: FsyMonth[] = [month],
  storage: Storage | null = defaultStorage(),
): HTMLElement {
  const page = document.createElement('div');
  page.className = 'fsy-month';
  page.dataset.route = 'fsy-month';
  page.dataset.monthId = month.id;
  const upcoming = getUpcomingFsySunday(today, month.year, month.month);

  const header = document.createElement('header');
  header.className = 'fsy-month-hero';
  const headerInner = document.createElement('div');
  headerInner.className = 'page-width';
  const back = routerLink('/fsy', 'All FSY months', 'fsy-back-link');
  const label = document.createElement('p');
  label.className = 'eyebrow';
  label.textContent = month.monthLabel;
  const title = document.createElement('h1');
  title.textContent = month.topic;
  const reference = document.createElement('p');
  reference.className = 'fsy-month-hero__reference';
  reference.textContent = month.guideReference;
  headerInner.append(back, createMonthNavigation(months, month.id), label, title, reference);
  if (upcoming) headerInner.append(createUpcomingBadge(upcoming));
  headerInner.append(createMonthAttribution(month));
  header.append(headerInner);

  const main = document.createElement('div');
  main.className = 'fsy-month__body page-width';

  const introItem = month.items.find((item) => item.type === 'intro');
  if (introItem) main.append(createItem(month.id, introItem, upcoming, storage));

  const scheduledItems = month.items.filter(
    (item) => item.week !== undefined,
  );
  const lessons = document.createElement('section');
  lessons.className = 'fsy-lessons';
  lessons.setAttribute('aria-label', `${month.monthLabel} lessons`);
  for (const item of scheduledItems.filter((entry) => entry.week !== 4)) {
    lessons.append(createItem(month.id, item, upcoming, storage));
  }

  const lastSundayItems = scheduledItems.filter((item) => item.week === 4);
  if (lastSundayItems.length > 0) {
    const lastSunday = document.createElement('section');
    lastSunday.className = 'fsy-last-sunday-group';
    lastSunday.setAttribute('aria-label', 'Last Sunday lessons');
    const heading = document.createElement('h2');
    heading.className = 'fsy-group-heading';
    heading.textContent = 'Last Sunday';
    const grid = document.createElement('div');
    grid.className = 'fsy-last-sunday-grid';
    for (const item of lastSundayItems) {
      grid.append(createItem(month.id, item, upcoming, storage));
    }
    lastSunday.append(heading, grid);
    lessons.append(lastSunday);
  }
  main.append(lessons);

  const activityItems = month.items.filter(
    (item) => item.type === 'activity-idea',
  );
  if (activityItems.length > 0) {
    const extra = document.createElement('section');
    extra.className = 'fsy-extra-content';
    extra.setAttribute('aria-label', 'Youth activity ideas');
    const heading = document.createElement('h2');
    heading.className = 'fsy-group-heading';
    heading.textContent = 'Youth activity ideas';
    const grid = document.createElement('div');
    grid.className = 'fsy-extra-content__grid';
    for (const item of activityItems) {
      grid.append(createItem(month.id, item, upcoming, storage));
    }
    extra.append(heading, grid);
    main.append(extra);
  }

  page.append(header, main);
  return page;
}
